import { groupBy, mapBy } from '@go-shared/lib/functional';
import { dateToIsoUTC, getLocaleDateStringUTC, isoToDateUTC } from '@go-shared/lib/time';
import {
  ReservableEventTemplateSnapshot,
  ReservableEventWithTimeslots,
} from '@go-shared/types/reservable';
import { Reservation } from '@go-shared/types/reservation';
import { SimpleUser } from '@go-shared/types/user';
import { FormikProps } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import {
  BubbleButton,
  BubbleCheckbox,
  BubbleCurrency,
  BubbleFormGroup,
  BubbleHintSelect,
} from './BubbleUI';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from './BubbleUI/BubbleTable';
import BubbleTable from './BubbleUI/BubbleTable';
import OccurrenceFiller from './OccurrenceFiller';
import {
  ReservationFormikForm,
  ReservationFormikOccurrence,
  ReservationFormikProps,
} from './Formik/ReservationFormik';
import { buildReserveSlotKey, buildReserveTimeslotLabel } from '@go-shared/lib/reserve';
import { ObjectId } from 'bson';
import { ReserveScheduleSlotPriceOverrides } from '@go-shared/types/reserveSchedule';
import { formatCentsToUSD, makeFullName } from '@go-shared/lib/utils';
import { createPortal } from 'react-dom';
import { usePublicServerInfo } from '@go-client/context/PublicServerInfoContext';

export interface ReservationDateTimeFillerProps {
  actionsElem?: HTMLDivElement;
  type: ReservationFormikProps['type'];
  users: SimpleUser[];
  eventSnapshot: ReservableEventTemplateSnapshot;
  formik: FormikProps<ReservationFormikForm>;
  reservation?: Reservation;
  readOnly?: boolean;
  reservableDetails?: ReservableEventWithTimeslots;
  reservableDetailsLoading?: boolean;
}

interface OccurrenceTableItem {
  key: string; // string idx used as key
  occurrence: string;
  date?: string;
  time?: string;
  host?: string;
  rental?: string;
  hasErrors: boolean;
  _isMain?: boolean;
  _occ: ReservationFormikOccurrence;
  _idx: number; // -1 for the main row
}

export default function ReservationDateTimeFiller(props: ReservationDateTimeFillerProps) {
  const { formik, eventSnapshot, users, readOnly, reservableDetails, reservableDetailsLoading } =
    props;
  const { values, setValues, setFieldValue } = formik;
  const { serverDate } = usePublicServerInfo();

  const [showQuickSet, setShowQuickSet] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [showUnavailableTimeslots, setShowUnavailableTimeslots] = useState(true);

  const ticketQuantities = Object.values(groupBy(values.tickets, 'ticketId')).map(group => ({
    ticket: { _id: group[0].ticketId },
    quantity: group.length,
  }));

  const ticketsMap = mapBy(eventSnapshot.reservables.tickets, t => t._id);
  const usersDict = mapBy(users, '_id');

  const mainOccurrence: ReservationFormikOccurrence = {
    date: values.date,
    time: values.time,
    timeDisplay: values.timeDisplay,
    duration: values.duration,
    host: values.host,
    rental: values.rental,
  };

  const [quickSetState, setQuickSetState] = useState<ReservationFormikOccurrence>({
    date: mainOccurrence.date || serverDate.iso,
    time: undefined,
  });

  function findNextOccurrence(): ReservationFormikOccurrence {
    // TODO: use findDayspan to get nearest occurrence
    return {
      _id: new ObjectId().toString(),
      date: values.date,
      time: undefined,
    };
  }

  function occurrenceTimeLabel(occ: ReservationFormikOccurrence) {
    if (!occ.time) return undefined;

    const matchingKey = buildReserveSlotKey(
      { date: occ.date, rental: occ.rental, host: occ.host },
      occ.time,
      occ.duration
    );

    const matchingSlot = reservableDetails?.timeslots.find(
      s =>
        buildReserveSlotKey(
          { date: dateToIsoUTC(s.date), rental: s.rental, host: s.host },
          s.time,
          s.duration
        ) === matchingKey
    );

    return buildReserveTimeslotLabel({
      label: matchingSlot?.label,
      time: occ.time,
      timeDisplay: occ.timeDisplay,
      duration: occ.duration,
      dayspan: matchingSlot?.dayspan,
    });
  }

  const occurrences = values.additionalOccurrences ?? [];

  const hostName = (hostId?: string) => {
    const u = hostId ? usersDict[hostId] : undefined;
    return u ? makeFullName(u.firstName, u.lastName) : undefined;
  };

  const mainTableItem: OccurrenceTableItem = {
    key: 'main',
    occurrence: 'Occurrence 1 (Main)',
    date: getLocaleDateStringUTC(isoToDateUTC(mainOccurrence.date)),
    time: occurrenceTimeLabel(mainOccurrence),
    host: hostName(mainOccurrence.host),
    rental: ticketsMap[mainOccurrence.rental ?? '']?.name,
    hasErrors: !mainOccurrence.time,
    _isMain: true,
    _occ: mainOccurrence,
    _idx: -1,
  };

  const tableItems: OccurrenceTableItem[] = occurrences.map((occ, idx) => ({
    key: occ._id ?? String(idx),
    occurrence: `Occurrence ${idx + 2}`,
    date: getLocaleDateStringUTC(isoToDateUTC(occ.date)),
    time: occurrenceTimeLabel(occ),
    host: hostName(occ.host),
    rental: ticketsMap[occ.rental ?? '']?.name,
    hasErrors: !occ.time,
    _occ: occ,
    _idx: idx,
  }));

  const selectedOcc =
    selectedKey === 'main'
      ? mainOccurrence
      : selectedKey && selectedKey !== 'main'
      ? occurrences.find(o => o._id === selectedKey)
      : undefined;

  function makeRowTdProps(
    data: OccurrenceTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const isExpanded = data.key === selectedKey;
    const isPast = data._occ.date && data._occ.date < serverDate.iso;

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps?.className,
        isExpanded ? 'bg-bubblegum-lightest' : undefined,
        isPast ? 'text-smoke italic' : undefined,
      ]
        .filter(Boolean)
        .join(' '),
    };
  }

  const tableFields: BubbleTableFields<OccurrenceTableItem>[] = [
    { key: 'occurrence' },
    { key: 'date' },
    { key: 'time' },
    { key: 'host' },
    { key: 'rental' },
    { key: 'hasErrors', label: '', behavior: BubbleTableFieldBehavior.MinWidth },
    { key: 'actions', label: '', behavior: BubbleTableFieldBehavior.Condense },
  ];

  const tableTemplates: BubbleTableTemplate<OccurrenceTableItem>[] = [
    { key: 'occurrence', tdProps: makeRowTdProps },
    { key: 'date', tdProps: makeRowTdProps },
    { key: 'time', tdProps: makeRowTdProps },
    { key: 'rental', tdProps: makeRowTdProps },
    { key: 'host', tdProps: makeRowTdProps },
    {
      key: 'hasErrors',
      tdProps: makeRowTdProps,
      render: item =>
        item.hasErrors ? (
          <div className="flex items-center text-crimson font-semibold">
            <FontAwesomeIcon icon="exclamation-triangle" />
            <span className="ml-1">Incomplete</span>
          </div>
        ) : (
          <span></span>
        ),
    },
    {
      key: 'actions',
      tdProps: makeRowTdProps,
      render: item => (
        <div className="flex items-center">
          <BubbleButton
            variant="navy"
            className="p-1-i"
            inverse
            icon="edit"
            onClick={() => setSelectedKey(item.key === selectedKey ? null : item.key)}
          />
          {!readOnly && item.key !== 'main' && (
            <BubbleButton
              variant="crimson"
              inverse
              className="p-2-i"
              icon="trash-alt"
              onClick={() => {
                setFieldValue(
                  'additionalOccurrences',
                  occurrences.filter(o => o._id !== item._occ._id)
                );
                setSelectedKey(null);
              }}
            />
          )}
        </div>
      ),
    },
  ];

  const priceOverrideOpts = eventSnapshot.reservables.tickets.map(t => t._id);

  function getAvailablePriceOverrideOpts(
    overrides: ReserveScheduleSlotPriceOverrides | undefined,
    currentKey?: string
  ) {
    return priceOverrideOpts.filter(
      o =>
        !Object.keys(overrides || {})
          .filter(k => k !== currentKey)
          .includes(o)
    );
  }

  function nextAvailablePriceOverrideOpt(overrides: ReserveScheduleSlotPriceOverrides | undefined) {
    return getAvailablePriceOverrideOpts(overrides)[0];
  }

  function mainOccurrenceFillerMaker(prefillOccurrences?: boolean) {
    return (
      <OccurrenceFiller
        onSetAdditionalOccurrences={
          prefillOccurrences
            ? (occurrences, main, priceOverrides) => {
                setValues({
                  ...values,
                  ...main,
                  additionalOccurrences: occurrences,
                  ...(priceOverrides !== undefined ? { priceOverrides } : {}),
                });
                setShowQuickSet(false);
              }
            : undefined
        }
        hideAdvancedMode={prefillOccurrences}
        values={prefillOccurrences ? quickSetState : mainOccurrence}
        onChange={(next, priceOverrides) =>
          prefillOccurrences
            ? setQuickSetState({ ...quickSetState, ...next })
            : setValues({
                ...values,
                ...next,
                ...(priceOverrides !== undefined ? { priceOverrides } : {}),
              })
        }
        ticketQuantities={ticketQuantities}
        users={users}
        eventSnapshot={eventSnapshot}
        reservableDetails={reservableDetails}
        reservableDetailsLoading={reservableDetailsLoading}
        readOnly={readOnly}
        showUnavailableTimeslots={showUnavailableTimeslots}
      />
    );
  }

  const formActions = (
    <div className="flex items-center">
      <BubbleCheckbox
        label="Show unavailable timeslots"
        checked={showUnavailableTimeslots}
        onValue={setShowUnavailableTimeslots}
        subtitle="Unavailable slots include capacity blocked by guest carts and active reservations, including this one."
      />
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <div className="grid grid-cols-1 gap-6">
      {portalButtons}
      {!showQuickSet && (
        <div className="jumbo-link relative">
          <div className="jumbo-link-image" />
          <div
            className="absolute top-0 left-0 w-full h-full flex flex-col
         justify-end items-start"
          >
            <div className="flex ml-3 mb-3">
              <BubbleButton
                className="shadow-md border border-white-darkest canvas-button stretched-link mr-3"
                variant="white"
                icon="bolt-lightning"
                disabled={readOnly}
                onClick={() => {
                  setShowQuickSet(true);
                  setSelectedKey(null);
                }}
              >
                Find a Spot
              </BubbleButton>
            </div>
          </div>
        </div>
      )}
      {showQuickSet && (
        <div className="bg-white shadow-xl border rounded-xl overflow-hidden">
          <div className="bg-turquoise text-white pl-4 font-bold flex items-center text-xl">
            <div className="text-base">Find a Spot</div>
            <BubbleButton
              className="ml-auto"
              size="large"
              onClick={() => setShowQuickSet(false)}
              icon="xmark"
              variant="white"
              inverse
            />
          </div>

          <div className="p-4">
            <div className="text-xs text-slate italic mb-4">
              Availability is based on what the guest can see. It includes exceptions and booking
              window settings. Clicking a time slot here will automatically set all dates in the
              section below.
            </div>
            {mainOccurrenceFillerMaker(true)}
          </div>
        </div>
      )}
      <BubbleFormGroup label="Dates">
        <div className="bg-white shadow p-4 border rounded grid grid-cols-1 gap-4 justify-items-start">
          <div className="text-xs text-slate italic">
            Make fine tuned edits. Changes here only affect the selected date.
          </div>
          <BubbleTable
            responsive
            striped
            slim
            hideHeader
            bordered
            items={[mainTableItem, ...tableItems]}
            fields={tableFields}
            templates={tableTemplates}
            detailsKey={selectedKey ?? ''}
            onRowClick={item => setSelectedKey(item.key === selectedKey ? null : item.key)}
            detailsClassName="p-4 bg-bubblegum-lightest"
            detailsRender={() =>
              selectedOcc ? (
                <div className="w-full bg-eggshell shadow p-3 border">
                  {selectedKey === 'main' ? (
                    mainOccurrenceFillerMaker(false)
                  ) : (
                    <OccurrenceFiller
                      values={selectedOcc}
                      onChange={next => {
                        setFieldValue(
                          'additionalOccurrences',
                          occurrences.map(o => (o._id === selectedOcc?._id ? next : o))
                        );
                      }}
                      ticketQuantities={ticketQuantities}
                      users={users}
                      eventSnapshot={eventSnapshot}
                      reservableDetails={reservableDetails}
                      reservableDetailsLoading={reservableDetailsLoading}
                      readOnly={readOnly}
                      showUnavailableTimeslots={showUnavailableTimeslots}
                    />
                  )}
                </div>
              ) : null
            }
          />
          {!readOnly && (
            <BubbleButton
              variant="navy"
              inverse
              className="p-0-i"
              icon="plus"
              onClick={() => {
                const newOcc = findNextOccurrence();
                setFieldValue('additionalOccurrences', [...occurrences, newOcc]);
                setSelectedKey(newOcc._id!);
              }}
            >
              Add Occurrence
            </BubbleButton>
          )}
        </div>
      </BubbleFormGroup>
      <BubbleFormGroup label="Price Overrides">
        <div className="bg-white shadow p-4 border rounded grid grid-cols-1 gap-4 justify-items-start">
          <div className="text-xs text-slate italic">
            Auto populates from the main timeslot selected if exists. Use to override the price for
            specific tickets for this reservation.
          </div>
          {Object.entries(values.priceOverrides || {}).map(([key, value]) => (
            <div key={key} className="w-full grid grid-cols-2 gap-4 items-start">
              <BubbleFormGroup label="Ticket">
                <BubbleHintSelect
                  value={key}
                  allowUndefined={false}
                  options={getAvailablePriceOverrideOpts(values.priceOverrides, key)}
                  labelExtractor={opt =>
                    ticketsMap[opt] ? `Ticket: ${ticketsMap[opt].name}` : opt
                  }
                  onValue={newKey => {
                    const next: ReserveScheduleSlotPriceOverrides = {
                      ...values.priceOverrides,
                      [newKey]: value,
                    };
                    delete next[key];
                    setFieldValue('priceOverrides', next);
                  }}
                />
              </BubbleFormGroup>
              <div className="flex items-center">
                <BubbleFormGroup
                  label={`Price${
                    ticketsMap[key] ? ` — base: ${formatCentsToUSD(ticketsMap[key].price)}` : ''
                  }`}
                  className="flex-grow"
                >
                  <BubbleCurrency
                    block
                    value={value}
                    onValue={val => {
                      if (val === undefined) return;
                      setFieldValue('priceOverrides', { ...values.priceOverrides, [key]: val });
                    }}
                    readOnly={readOnly}
                  />
                </BubbleFormGroup>
                {!readOnly && (
                  <BubbleButton
                    style={{ alignSelf: 'end' }}
                    variant="crimson"
                    inverse
                    className="-mr-3"
                    icon="trash-alt"
                    onClick={() => {
                      const next = { ...values.priceOverrides };
                      delete next[key];
                      setFieldValue('priceOverrides', next);
                    }}
                  />
                )}
              </div>
            </div>
          ))}
          {!readOnly && (
            <BubbleButton
              variant="navy"
              inverse
              className="p-0-i"
              icon="plus"
              disabled={!nextAvailablePriceOverrideOpt(values.priceOverrides)}
              onClick={() => {
                const nextKey = nextAvailablePriceOverrideOpt(values.priceOverrides);
                if (!nextKey) return;
                setFieldValue('priceOverrides', {
                  ...(values.priceOverrides || {}),
                  [nextKey]: ticketsMap[nextKey]?.price ?? 0,
                });
              }}
            >
              Add Price Override
            </BubbleButton>
          )}
        </div>
      </BubbleFormGroup>
    </div>
  );
}
